import React, { useState, useEffect } from 'react';
import { CheckCircle, X, Zap, Crown, Star, Gem, Sparkles, Users, Shield, ArrowRight, Leaf, Radar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config/apiBase';
import { useFeature } from '../context/FeaturesContext';

const API = API_BASE;

const PLAN_META = {
    free: { icon: Star, color: '#6B7280', popular: false },
    lite: { icon: Leaf, color: '#10B981', popular: false },
    start: { icon: Zap, color: '#F59E0B', popular: true },
    gestao: { icon: Crown, color: '#3B82F6', popular: false },
    pro: { icon: Gem, color: '#8B5CF6', popular: false },
    enterprise: { icon: Sparkles, color: 'var(--sv-accent)', popular: false },
};

const FEATURES_TABLE = [
    { label: 'Colaboradores', free: '1', lite: '2', start: '3', gestao: '10', pro: '25', enterprise: 'Ilimitado' },
    { label: 'Gestores', free: '0', lite: '0', start: '1', gestao: '3', pro: '5', enterprise: 'Ilimitado' },
    { label: 'Lançamentos financeiros/mês', free: '10', lite: '30', start: '100', gestao: 'Ilimitado', pro: 'Ilimitado', enterprise: 'Ilimitado' },
    { label: 'Dashboard', free: true, lite: true, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Gestão básica', free: true, lite: true, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Copilot IA', free: true, lite: true, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Suporte', free: true, lite: true, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Logo da empresa', free: false, lite: true, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Financeiro básico', free: false, lite: true, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Ponto Digital inicial', free: false, lite: true, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Exportação CSV', free: false, lite: 'Simples', start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Ponto Digital funcional', free: false, lite: false, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Solicitações', free: false, lite: true, start: true, gestao: true, pro: true, enterprise: true },
    { label: 'Performance', free: false, lite: false, start: false, gestao: true, pro: true, enterprise: true },
    { label: 'Financeiro completo', free: false, lite: false, start: false, gestao: true, pro: true, enterprise: true },
    { label: 'Radar de Ofertas', free: false, lite: false, start: 'Opcional', gestao: true, pro: 'Premium', enterprise: 'Premium' },
    { label: 'Auditoria', free: false, lite: false, start: false, gestao: 'Resumida', pro: 'Completa', enterprise: 'Completa' },
    { label: 'Escalas e jornadas', free: false, lite: false, start: false, gestao: 'Básicas', pro: true, enterprise: true },
    { label: 'Exportação PDF', free: false, lite: false, start: false, gestao: true, pro: true, enterprise: true },
    { label: 'Permissões avançadas', free: false, lite: false, start: false, gestao: false, pro: true, enterprise: true },
    { label: 'Relatórios avançados', free: false, lite: false, start: false, gestao: false, pro: true, enterprise: true },
    { label: 'Geofence avançado', free: false, lite: false, start: false, gestao: false, pro: true, enterprise: true },
    { label: 'Ponto Digital premium', free: false, lite: false, start: false, gestao: false, pro: true, enterprise: true },
    { label: 'Suporte dedicado', free: false, lite: false, start: false, gestao: false, pro: false, enterprise: true },
];

const RADAR_LABELS = ['Sem acesso', 'Muito baixa', 'Baixa', 'Media', 'Alta', 'Maxima'];

function FeatureCell({ value }) {
    if (value === true) return <CheckCircle size={14} style={{ color: '#22c55e' }} />;
    if (value === false) return <X size={14} style={{ color: '#374151' }} />;
    return <span className="text-[11px] font-medium" style={{ color: 'var(--sv-accent)' }}>{value}</span>;
}

export default function PricingPage() {
    const showPlans = useFeature('show_plans');
    const [plans, setPlans] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const navigate = useNavigate();

    // Pausado: enquanto show_plans=false, qualquer acesso a /planos volta pra home.
    useEffect(() => {
        if (!showPlans) navigate('/', { replace: true });
    }, [navigate, showPlans]);

    useEffect(() => {
        if (!showPlans) return;
        fetch(`${API}/api/payments/plans`).then(r => r.json()).then(d => setPlans(d)).catch(() => {});
    }, [showPlans]);

    if (!showPlans) return null;

    const allPlans = [
        { slug: 'free', name: 'Free', amount: 0, max_employees: 1, max_gestores: 0, radar_priority: 0, features: ['Dashboard básico', 'Até 1 colaborador', 'Copilot IA básico', 'Gestão básica', 'Alertas básicos'] },
        { slug: 'lite', name: 'Lite', amount: 9.90, max_employees: 2, max_gestores: 0, radar_priority: 1, features: ['Tudo do Free', 'Logo da empresa', 'Financeiro básico', 'Ponto Digital inicial', 'CSV simples', 'Histórico básico'] },
        { slug: 'start', name: 'Start', amount: 19.90, max_employees: 3, max_gestores: 1, radar_priority: 2, features: ['Tudo do Lite', 'Ponto Digital funcional', '1 gestor opcional', 'Solicitações', 'CSV melhor', 'Identidade visual'] },
        { slug: 'gestao', name: 'Gestão', amount: 79.90, max_employees: 10, max_gestores: 3, radar_priority: 3, features: ['Tudo do Start', 'Performance', 'Financeiro completo', 'Radar de Ofertas', 'Auditoria resumida', 'Escalas básicas', 'PDF'] },
        { slug: 'pro', name: 'Pro', amount: 149.90, max_employees: 25, max_gestores: 5, radar_priority: 4, features: ['Tudo do Gestão', 'Permissões avançadas', 'Auditoria completa', 'Radar premium', 'Relatórios avançados', 'Geofence avançado'] },
        { slug: 'enterprise', name: 'Enterprise', amount: 0, max_employees: 9999, max_gestores: 9999, radar_priority: 5, features: ['Tudo do Pro', 'Ilimitado', 'Suporte dedicado', 'SLA garantido', 'Multi-unidades (futuro)', 'Configurações personalizadas'] },
    ];

    const displayPlans = allPlans.map(p => {
        const apiPlan = plans.find(a => a.slug === p.slug);
        return apiPlan ? { ...p, ...apiPlan, features: p.features, name: p.name } : p;
    });

    const handleCTA = (slug) => {
        if (slug === 'enterprise') return;
        navigate('/login');
    };

    return (
        <div style={{ background: '#071A3A', minHeight: '100vh' }}>
            {/* Header */}
            <div className="text-center pt-16 pb-10 px-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--sv-accent)' }}>Planos SuperVitrine</p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4" style={{ color: '#F5F5F0', fontFamily: "var(--font-display)", letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                    O plano certo para cada<br />fase do seu negócio
                </h1>
                <p className="text-sm sm:text-base max-w-xl mx-auto" style={{ color: '#8A8A7A', lineHeight: 1.7 }}>
                    Comece grátis. Faça upgrade quando precisar. Cancele quando quiser.
                </p>
            </div>

            {/* Plans Grid */}
            <div className="max-w-6xl mx-auto px-4 pb-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {displayPlans.map((p) => {
                        const meta = PLAN_META[p.slug] || PLAN_META.free;
                        const PIcon = meta.icon;
                        const isPopular = meta.popular;
                        const isEnterprise = p.slug === 'enterprise';

                        return (
                            <div key={p.slug} className="rounded-2xl p-5 relative flex flex-col transition-all hover:scale-[1.02]"
                                style={{
                                    background: isPopular ? 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.02) 100%)' : '#111111',
                                    border: isPopular ? '2px solid rgba(245,158,11,0.30)' : '1px solid rgba(125,165,255,0.16)',
                                }}
                                data-testid={`pricing-plan-${p.slug}`}>
                                {isPopular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <span className="text-[10px] px-3 py-1 rounded-full font-bold" style={{ background: '#F59E0B', color: '#071A3A' }}>MAIS POPULAR</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 mb-4 mt-1">
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}15` }}>
                                        <PIcon size={16} style={{ color: meta.color }} />
                                    </div>
                                    <span className="text-base font-bold" style={{ color: '#F5F5F0' }}>{p.name}</span>
                                </div>

                                <div className="mb-4">
                                    {p.amount > 0 ? (
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-2xl font-bold" style={{ color: meta.color }}>R$ {p.amount.toFixed(2).replace('.', ',')}</span>
                                            <span className="text-xs" style={{ color: '#6B7280' }}>/mes</span>
                                        </div>
                                    ) : isEnterprise ? (
                                        <span className="text-lg font-bold" style={{ color: meta.color }}>Sob consulta</span>
                                    ) : (
                                        <span className="text-2xl font-bold" style={{ color: meta.color }}>Grátis</span>
                                    )}
                                </div>

                                <div className="flex gap-3 mb-4 text-[11px]" style={{ color: '#8A8A7A' }}>
                                    <span className="flex items-center gap-1"><Users size={10} /> {p.max_employees >= 9999 ? 'Ilimitado' : p.max_employees} colab.</span>
                                    <span className="flex items-center gap-1"><Shield size={10} /> {p.max_gestores >= 9999 ? 'Ilimitado' : p.max_gestores} gestor(es)</span>
                                </div>

                                <div className="flex-1 space-y-2 mb-5">
                                    {(p.features || []).map((f, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <CheckCircle size={12} className="shrink-0 mt-0.5" style={{ color: meta.color }} />
                                            <span className="text-[12px]" style={{ color: '#C4C4B8' }}>{f}</span>
                                        </div>
                                    ))}
                                </div>

                                <button onClick={() => handleCTA(p.slug)}
                                    className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                                    style={{
                                        background: isPopular ? '#F59E0B' : isEnterprise ? 'transparent' : `${meta.color}15`,
                                        color: isPopular ? '#0A0A0A' : meta.color,
                                        border: isEnterprise ? `1px solid ${meta.color}40` : 'none',
                                    }}
                                    data-testid={`pricing-cta-${p.slug}`}>
                                    {p.slug === 'free' && <>Começar grátis <ArrowRight size={14} /></>}
                                    {p.slug === 'lite' && <>Assinar Lite <ArrowRight size={14} /></>}
                                    {p.slug === 'start' && <>Assinar Start <ArrowRight size={14} /></>}
                                    {p.slug === 'gestao' && <>Assinar Gestão <ArrowRight size={14} /></>}
                                    {p.slug === 'pro' && <>Assinar Pro <ArrowRight size={14} /></>}
                                    {p.slug === 'enterprise' && 'Fale com a gente'}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Add-ons */}
            <div className="max-w-4xl mx-auto px-4 pb-10">
                <div className="text-center mb-6">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--sv-accent)' }}>Módulos extras</p>
                    <h2 className="text-lg font-bold" style={{ color: '#F5F5F0' }}>Complemente sua operação</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                        { name: 'Suporte Prioritário', price: 'R$ 29,90/mês', desc: 'Prioridade no atendimento e onboarding assistido' },
                        { name: 'Usuários Extras', price: 'R$ 4,90/usuário/mês', desc: 'Adicione colaboradores ou gestores além do limite' },
                        { name: 'Radar de Ofertas', price: 'R$ 14,90/mês', desc: 'Pesquisa de produtos, alertas e monitoramento' },
                    ].map((a, i) => (
                        <div key={i} className="rounded-xl p-4" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                            <p className="text-sm font-semibold mb-1" style={{ color: '#F5F5F0' }}>{a.name}</p>
                            <p className="text-xs font-bold mb-2" style={{ color: 'var(--sv-accent)' }}>{a.price}</p>
                            <p className="text-[11px]" style={{ color: '#8A8A7A' }}>{a.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Feature comparison toggle */}
            <div className="max-w-6xl mx-auto px-4 pb-16">
                <div className="text-center mb-6">
                    <button onClick={() => setShowTable(!showTable)} className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
                        style={{ background: showTable ? 'rgba(var(--sv-accent-rgb), 0.12)' : '#111111', color: 'var(--sv-accent)', border: '1px solid rgba(var(--sv-accent-rgb), 0.25)' }}
                        data-testid="pricing-compare-toggle">
                        {showTable ? 'Recolher comparativo' : 'Ver comparativo completo'}
                    </button>
                </div>

                {showTable && (
                    <div className="overflow-x-auto rounded-xl" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                        <table className="w-full text-left" data-testid="pricing-comparison-table">
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(125,165,255,0.16)' }}>
                                    <th className="py-3 px-4 text-[11px] font-semibold" style={{ color: '#8A8A7A' }}>Recurso</th>
                                    {['Free', 'Lite', 'Start', 'Gestão', 'Pro', 'Enterprise'].map(n => (
                                        <th key={n} className="py-3 px-3 text-[11px] font-semibold text-center" style={{ color: PLAN_META[n.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")]?.color || '#8A8A7A' }}>{n}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {FEATURES_TABLE.map((row, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #0f0f0f' }}>
                                        <td className="py-2.5 px-4 text-[11px]" style={{ color: '#C4C4B8' }}>{row.label}</td>
                                        {['free', 'lite', 'start', 'gestao', 'pro', 'enterprise'].map(slug => (
                                            <td key={slug} className="py-2.5 px-3 text-center"><FeatureCell value={row[slug]} /></td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* CTA bottom */}
            <div className="text-center pb-16 px-4">
                <div className="max-w-lg mx-auto rounded-2xl p-8" style={{ background: 'rgba(var(--sv-accent-rgb), 0.05)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                    <h3 className="text-lg font-bold mb-2" style={{ color: '#F5F5F0' }}>Comece grátis agora</h3>
                    <p className="text-xs mb-5" style={{ color: '#8A8A7A' }}>Crie sua conta em segundos. Sem cartão de crédito.</p>
                    <button onClick={() => navigate('/login')} className="px-8 py-3 rounded-xl text-sm font-bold"
                        style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="pricing-bottom-cta">
                        Criar conta grátis
                    </button>
                </div>
            </div>
        </div>
    );
}
